import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Breadcrumbs,
  Chip,
  Avatar,
  Button,
  Divider,
  Paper,
  Grid,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ShareIcon from '@mui/icons-material/Share';

// Market Trends articles
const marketTrendsArticles = [
  {
    id: 'market-trends-1',
    title: "The Smart Landlord's Guide: Why Property Management is a Game-Changer",
    description: "Discover how professional property management can transform your rental investment from a full-time headache into truly passive income. Learn the six key benefits that are making smart landlords switch to professional management.",
    image: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1073&q=80",
    author: "Lantena Team",
    readTime: "5 min read",
    date: "March 25, 2025",
    category: "Market Trends",
    content: `
      <h2>Owning Property is Great – Managing it? Not So Much.</h2>
      <p>If you're a property owner, you know that real estate is one of the best investments out there. But let's be honest: owning rental property and managing it successfully are two very different things.</p>
      
      <p>Many landlords start with a simple goal—passive income. But soon, reality sets in:</p>
      <ul style="list-style-type: none; padding-left: 20px; margin-bottom: 20px;">
        <li>• Chasing tenants for rent payments</li>
        <li>• Endless maintenance requests</li>
        <li>• Late-night emergency calls</li>
        <li>• Dealing with vacancies and tenant disputes</li>
        <li>• Navigating complex MRI tax compliance and KRA regulations</li>
      </ul>
      
      <p>Suddenly, your "passive" income feels more like a full-time job. But what if there was a smarter way?</p>
      <p>That's where Lantena Property Managers comes in.</p>
      
      <h2>We Don't Just Manage Properties – We Transform the Landlord Experience</h2>
      <p>At Lantena, we believe property ownership should be stress-free and profitable. We've built an efficient, technology-driven property management system that gives landlords peace of mind and full control without the day-to-day headaches.</p>
      
      <h2>Why Smart Landlords Choose Lantena</h2>
      
      <h3>1. Automated Rent Collection – No More Chasing Tenants</h3>
      <p>Rent goes directly into your account—on time, every time. Our system ensures tenants receive automatic reminders and have multiple payment options to make rent collection seamless.</p>
      
      <h3>2. Real-Time Property Monitoring & Reporting</h3>
      <p>Stay fully informed without being overwhelmed. Our cutting-edge management software gives you a live dashboard showing:</p>
      <ul style="list-style-type: none; padding-left: 20px; margin-bottom: 20px;">
        <li>• Which tenants have paid</li>
        <li>• Outstanding balances</li>
        <li>• Maintenance requests</li>
        <li>• Revenue & expense reports</li>
      </ul>
      <p>You don't need to visit your property—your property comes to you.</p>
      
      <h3>3. Professional Tenant Management & Vetting</h3>
      <p>Good tenants make all the difference. We:</p>
      <ul style="list-style-type: none; padding-left: 20px; margin-bottom: 20px;">
        <li>• Screen and vet all tenants thoroughly</li>
        <li>• Handle lease agreements and renewals</li>
        <li>• Manage tenant communication and concerns</li>
        <li>• Enforce property rules professionally</li>
      </ul>
      
      <h3>4. Fast, Reliable Property Maintenance & Repairs</h3>
      <p>A leaking roof? A broken water pipe? Our network of trusted service providers ensures that maintenance requests are addressed quickly and cost-effectively—without landlords having to lift a finger.</p>
      
      <h3>5. MRI Tax Compliance – Stay on the Right Side of KRA</h3>
      <p>Kenya's Monthly Rental Income (MRI) tax laws can be overwhelming, and non-compliance leads to hefty penalties. Lantena ensures:</p>
      <ul style="list-style-type: none; padding-left: 20px; margin-bottom: 20px;">
        <li>• Accurate monthly MRI tax calculations</li>
        <li>• Timely tax return filing</li>
        <li>• KRA compliance support & dispute resolution</li>
      </ul>
      <p>With us, you stay compliant and save money by taking advantage of allowable tax deductions.</p>
      
      <h3>6. Security, Cleaning & Rubbish Collection</h3>
      <p>We don't just manage your tenants—we manage the entire property environment. Our services include:</p>
      <ul style="list-style-type: none; padding-left: 20px; margin-bottom: 20px;">
        <li>• Professional security services (guards & CCTV solutions)</li>
        <li>• Regular cleaning and garbage collection</li>
        <li>• Common area maintenance</li>
      </ul>
      <p>A well-maintained property means higher tenant satisfaction and fewer vacancies.</p>
      
      <h2>Packages Tailored to Your Needs</h2>
      <p>At Lantena, we offer flexible property management packages to fit every landlord's needs:</p>
      
      <p><strong>🌙 Lantena Moonshot Package (Basic)</strong><br>
      Ideal for landlords who want professional rent collection and tenant management.</p>
      
      <p><strong>🌍 Lantena Savannah Package (Full Service)</strong><br>
      A complete solution, including tax support, maintenance, and security management.</p>
      
      <p><strong>💼 Custom Packages</strong><br>
      Tailored services based on your unique property needs.</p>
      
      <p>No two properties are the same, so we customize our management approach for the best results.</p>
      
      <h2>Why Lantena? Because Smart Landlords Work Smarter – Not Harder.</h2>
      <ul style="list-style-type: none; padding-left: 20px; margin-bottom: 20px;">
        <li>• <strong>We are Reliable.</strong> We handle every aspect of property management with professionalism and integrity.</li>
        <li>• <strong>We are Efficient.</strong> Our technology-driven approach makes property management smooth and transparent.</li>
        <li>• <strong>We are Professional.</strong> Years of experience, deep market knowledge, and a commitment to excellence set us apart.</li>
      </ul>
      
      <p>Owning rental property should be a wealth-building experience—not a burden. Let Lantena Property Managers take care of the stress while you enjoy the financial rewards of real estate investment.</p>
      
      <h2>📞 Let's Talk!</h2>
      <p>Whether you own one rental unit or an entire real estate portfolio, we have a solution for you.</p>
      <p>📍 Visit us: Ouru Complex, 3rd Floor (Above Equity), Kisii<br>
      📞 Call/WhatsApp: 0722575313 / 0736261808       📧 Email: lantenapmanagers@gmail.com<br>
      🌐 <a href="https://www.lantenapm.com" style="color: #f66b10; text-decoration: none;">www.lantenapm.com</a></p>
      
      <p style="font-weight: bold; color: #0a233b;">🔹 Lantena Property Managers – Your Trusted, Accountable and Transparent Property and Project Partner</p>
    `
  },
  {
    id: 'market-trends-2',
    title: "Mastering the Art of a Deal in Property and Project Management",
    description: "Learn the insider strategies that separate successful property investments from financial disasters. Discover how expert management techniques can protect your real estate portfolio from common pitfalls and maximize returns.",
    image: "https://images.unsplash.com/photo-1460317442991-0ec209397118?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1470&q=80",
    author: "Lantena Team",
    readTime: "7 min read",
    date: "March 20, 2025",
    category: "Market Trends",
    content: `
      <div style="font-size: 18px; line-height: 1.6;">
      <h2>Introduction</h2>
      <p>At Lantena Property Managers, we understand that success in property and project management is more than just handling tenants or overseeing construction—it's about crafting a vision, anticipating challenges, and structuring management strategies that create long-term value.</p>
      
      <p>Whether you're a landlord, investor, or project manager, the way you handle rent collection, maintenance, tax compliance, and risk mitigation can make or break your real estate journey. Over the years, we've seen properties flourish under strategic management while others deteriorate due to mismanagement. We've worked with landlords facing tenant disputes, taxation nightmares, and overwhelming maintenance costs.</p>
      
      <p>The difference between success and failure? Mastering the "art of a deal"—a blend of expertise, foresight, and deep market understanding.</p>
      
      <p>This guide isn't just another article—it's an expert insight into property and project management, shaped by real-world experience. If you own property, manage projects, or plan to invest in real estate, this will be one of the most valuable reads for you.</p>
      
      <h2>1. Understanding the Core of Property & Project Management</h2>
      <p>At its core, real estate management is about value, trust, and efficiency. Property owners don't just seek management services—they want financial stability, peace of mind, and future-proof strategies. At Lantena, we believe that great property management is proactive, structured, and built on deep market intelligence.</p>
      
      <h3>Key Principles of Effective Property & Project Management</h3>
      <ul style="list-style-type: none; padding-left: 20px; margin-bottom: 20px;">
        <li>• <strong>Proactive Problem-Solving</strong> – The best property managers don't wait for issues to escalate; they prevent them from happening in the first place.</li>
        <li>• <strong>Market Awareness</strong> – Understanding rental trends, maintenance costs, legal frameworks, and tenant expectations gives landlords a competitive advantage.</li>
        <li>• <strong>Due Diligence</strong> – Rushing into contractor agreements or leasing arrangements without thorough research is financial suicide. We always verify legal documents, financials, and contractual obligations.</li>
        <li>• <strong>Clear Communication</strong> – Transparency between property managers, landlords, and tenants ensures smoother operations and minimizes disputes.</li>
        <li>• <strong>Risk Management</strong> – Anticipating what could go wrong helps us stay ahead of financial and legal pitfalls.</li>
      </ul>
      
      <h2>2. Lessons from Our Experience in Property Management</h2>
      <p>With our years of experience in property and project management, we've learned that success is about precision, planning, and professionalism. Many landlords struggle with late rent payments, unexpected repair costs, and complex tax obligations. These challenges have helped us develop efficient systems that mitigate risks and maximize profitability.</p>
      
      <h3>2.1 How Lantena Solves Property Management Challenges</h3>
      <ul style="list-style-type: none; padding-left: 20px; margin-bottom: 20px;">
        <li>• <strong>Automated Rent Collection:</strong> Late or missed rent payments are a major challenge for landlords. We implement structured, automated systems that ensure timely collections through the landlord's account.</li>
        <li>• <strong>Tenant Screening and Lease Structuring:</strong> A single bad tenant can cost a landlord thousands in unpaid rent and legal fees. Our strict screening process and well-drafted leases protect landlords from such risks.</li>
        <li>• <strong>Proactive Maintenance Management:</strong> Regular property inspections and preventive maintenance help avoid costly emergency repairs.</li>
        <li>• <strong>Financial Reporting & Budgeting:</strong> Many landlords operate without tracking income and expenses. We provide detailed financial reporting to ensure profitability and tax compliance.</li>
      </ul>
      
      <h3>2.2 Tactical Management Techniques That Work</h3>
      <ul style="list-style-type: none; padding-left: 20px; margin-bottom: 20px;">
        <li>• <strong>Smart Tenant Agreements:</strong> A well-drafted lease protects landlords from legal and financial disputes.</li>
        <li>• <strong>Efficient Maintenance Scheduling:</strong> Predictable maintenance cycles help avoid last-minute, expensive repairs.</li>
        <li>• <strong>Technology-Driven Oversight:</strong> Our digital property management tools allow landlords to monitor rent payments, maintenance requests, and financial reports remotely.</li>
      </ul>
      
      <h2>3. Project Management: Why Structure Matters</h2>
      <p>Poor project management is a silent killer of real estate investments. At Lantena, we've rescued projects that were plagued by contractor delays, budget overruns, and lack of oversight. The secret? A structured approach to project execution.</p>
      
      <h3>3.1 Key Project Management Strategies</h3>
      <ul style="list-style-type: none; padding-left: 20px; margin-bottom: 20px;">
        <li>• Well-structured contracts protect your investment.</li>
        <li>• Cash flow management is critical for avoiding financial strain.</li>
        <li>• Choosing the cheapest contractor often leads to the most expensive mistakes.</li>
        <li>• Technology in project tracking helps you stay on budget and on schedule.</li>
      </ul>
      
      <h3>3.2 The Consequences of Poor Project Management</h3>
      <p>Without expert project oversight, property owners face:</p>
      <ul style="list-style-type: none; padding-left: 20px; margin-bottom: 20px;">
        <li>• Lost investments due to financial miscalculations</li>
        <li>• Tax complications and government penalties</li>
        <li>• Disputes with contractors and tenants</li>
        <li>• Structural failures and safety hazards</li>
      </ul>
      <p>At Lantena, we don't just manage projects—we ensure they succeed.</p>
      
      <h2>4. The Tax Factor: A Silent Killer for Landlords in Kenya</h2>
      <p>Many landlords underestimate the impact of tax compliance. In Kenya, rental income tax is mandatory, and the Kenya Revenue Authority (KRA) is relentless—ignorance is not an excuse.</p>
      
      <h3>4.1 Why You Need MRI Tax Compliance</h3>
      <p>The Monthly Rental Income (MRI) tax is a legal requirement, and non-compliance can result in:</p>
      <ul style="list-style-type: none; padding-left: 20px; margin-bottom: 20px;">
        <li>• Hefty fines and legal action</li>
        <li>• Frozen bank accounts</li>
        <li>• Loss of property due to unpaid tax liabilities</li>
      </ul>
      <p>Many landlords in Nairobi, Kisii, Mombasa, and beyond have had sleepless nights after realizing they owe years of unpaid tax. At Lantena, we ensure that your tax compliance is effortless and seamless.</p>
      
      <p>If you own property and haven't structured your tax compliance, you are sitting on a financial time bomb.</p>
      
      <h2>5. Case Studies: How Smart Management Saves Money</h2>
      
      <h3>Case Study 1: The Landlord Who Avoided a $10,000 Loss</h3>
      <p>A landlord in Westlands was facing tenant payment issues. By implementing automated rent collection and structured lease agreements, we turned his property from a cash-draining liability into a stable income-generating asset.</p>
      
      <h3>Case Study 2: Project Rescue Mission</h3>
      <p>A real estate investor in Dubai wanted to develop rental units in Kisii, Kenya. Due to poor contractor oversight, the project was 8 months behind schedule. By restructuring the project, enforcing milestone-based payments, and ensuring direct site supervision, we saved him from an 18% budget overrun.</p>
      
      <h2>6. Final Thoughts: Master the Art of a Deal or Pay the Price</h2>
      <p>If there's one thing we've learned at Lantena, it's this:</p>
      <ul style="list-style-type: none; padding-left: 20px; margin-bottom: 20px;">
        <li>• You either master the art of property management, or you risk financial disaster.</li>
        <li>• A mismanaged property can drain your finances.</li>
        <li>• Poor project oversight can ruin your investment.</li>
        <li>• Ignoring tax laws can cost you your property.</li>
      </ul>
      
      <p>If you're serious about your real estate investments, you need expert management. And that's where Lantena Property Managers come in.</p>
      
      <p style="background-color: #f5f5f5; padding: 15px; border-left: 4px solid #f66b10; margin: 20px 0;">
        📩 For a free consultation on optimizing your rental income and projects, talk to Lantena Property Managers today. We are here to help.
      </p>
      </div>
    `
  }
];

// Investment articles
const investmentArticles = [
  {
    id: 'investment-1',
    title: "Investment Strategies: Smart Homes & Projects for Diaspora Investors",
    description: "Discover how Kenyans abroad can leverage smart real estate investments, luxury renovations, and technology-driven property management to build wealth and create a lasting legacy in Kenya's growing property market.",
    image: "https://images.unsplash.com/photo-1626178793926-22b28830aa30?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80",
    author: "Lantena Team",
    readTime: "8 min read",
    date: "March 15, 2025",
    category: "Investment",
    content: `
      <div style="font-size: 18px; line-height: 1.6;">
      <h2>Investing in Smart Real Estate with Lantena Property Managers</h2>
      <p>The real estate sector is rapidly evolving, and for investors living abroad, there has never been a better time to invest in smart projects and luxury renovations in Kenya. The traditional property market is being disrupted by technology, automation, and the growing demand for high-end, efficiently managed properties. At Lantena Property Managers, we bridge the gap between investors and the future of real estate, offering seamless project management and luxury renovation solutions tailored for diaspora investors looking to make smart, lucrative, and sustainable investments.</p>
      
      <h2>Why Real Estate Investment in Kenya is a Smart Move</h2>
      <p>For Kenyans in the USA, Europe, Australia, the Middle East, and beyond, investing in real estate back home is more than just a business move—it's a legacy. The Kenyan property market is steadily growing, driven by urban expansion, increasing demand for rentals, and the rising middle class. The shift towards smart homes and sustainable real estate presents a golden opportunity to build wealth while embracing the future of living.</p>
      
      <p>Key drivers making Kenya a prime investment hub:</p>
      <ul style="list-style-type: none; padding-left: 20px; margin-bottom: 20px;">
        <li>✅ Rapid urbanization and population growth.</li>
        <li>✅ Increased demand for high-end rental properties.</li>
        <li>✅ Expanding infrastructure and government incentives.</li>
        <li>✅ Technological advancements in real estate.</li>
        <li>✅ High return on investment (ROI) in both residential and commercial properties.</li>
      </ul>
      
      <p>With these factors in mind, Lantena Property Managers provides strategic guidance to ensure every investment is optimized for success.</p>
      
      <h2>Smart Project Management: The Future of Investment</h2>
      <p>Investing in real estate is not just about acquiring property—it's about managing it efficiently. Smart Project Management integrates technology, design, and sustainability to create homes and developments that offer higher returns, lower maintenance costs, and long-term value.</p>
      
      <h3>How Lantena's Project Management Services Benefit You</h3>
      <ul style="list-style-type: none; padding-left: 20px; margin-bottom: 20px;">
        <li><strong>1️⃣ Turnkey Solutions</strong> – From site acquisition, design, approvals, construction, and finishing, we manage every phase of your project.</li>
        <li><strong>2️⃣ Smart Technology Integration</strong> – We incorporate automated security systems, energy-efficient designs, and smart home automation to increase property value and tenant desirability.</li>
        <li><strong>3️⃣ Budget & Cost Efficiency</strong> – We provide accurate cost estimates and budget control mechanisms to ensure your investment remains profitable.</li>
        <li><strong>4️⃣ Remote Investor Support</strong> – Our cutting-edge management software allows you to track progress in real-time, view financial statements, and monitor rental income from anywhere in the world.</li>
        <li><strong>5️⃣ Sustainable & Eco-Friendly Construction</strong> – We integrate green building techniques, energy-saving solutions, and modern aesthetics that appeal to the next generation of buyers and tenants.</li>
      </ul>
      
      <p>With Lantena Property Managers, you are not just developing property; you are future-proofing your investment.</p>
      
      <h2>Luxury Renovations: Transforming Properties into High-Value Assets</h2>
      <p>The demand for luxury apartments, smart homes, and high-end commercial properties in Kenya is rising. Diaspora investors looking to upgrade their existing properties or transform old buildings into premium assets can take advantage of Lantena's specialized luxury renovation services.</p>
      
      <h3>What Sets Lantena's Renovation Services Apart?</h3>
      <ul style="list-style-type: none; padding-left: 20px; margin-bottom: 20px;">
        <li>✔ <strong>Custom Design & High-End Finishing</strong> – We ensure a seamless blend of functionality and elegance, using premium materials, modern aesthetics, and cutting-edge interior design.</li>
        <li>✔ <strong>Smart Home Upgrades</strong> – Convert your property into a state-of-the-art residence with automated lighting, climate control, smart security systems, and energy-efficient installations.</li>
        <li>✔ <strong>Market-Driven Transformations</strong> – We renovate properties with the market in mind, ensuring every upgrade enhances rental yields and resale value.</li>
        <li>✔ <strong>End-to-End Renovation Management</strong> – From conceptualization to execution, we handle every detail, ensuring a stress-free experience for overseas investors.</li>
        <li>✔ <strong>Value Addition for Rentals & Resale</strong> – We position your property for high-end tenants and buyers, guaranteeing a return on investment that outperforms traditional developments.</li>
      </ul>
      
      <h2>Why Diaspora Investors Trust Lantena Property Managers</h2>
      <p>Lantena is not just a property management company—we are investment partners dedicated to making real estate ownership simple, secure, and highly profitable for Kenyans abroad. Here's why clients choose us:</p>
      
      <ul style="list-style-type: none; padding-left: 20px; margin-bottom: 20px;">
        <li>✅ <strong>Full Transparency & Real-Time Monitoring</strong> – Our management software ensures you are always in control, with detailed insights on your property's financial performance, tenant status, and project updates.</li>
        <li>✅ <strong>Seamless Rent Collection & Financial Management</strong> – Rental income is deposited directly into the landlord's account, ensuring full financial control with no intermediaries.</li>
        <li>✅ <strong>Regulatory Compliance & Tax Support</strong> – We ensure your property adheres to MRI Tax regulations, protecting you from legal liabilities and penalties.</li>
        <li>✅ <strong>Zero Hassle Property Management</strong> – We handle everything from tenant vetting, lease management, maintenance, and security to cleaning services, so you earn passive income without the stress.</li>
        <li>✅ <strong>Tailor-Made Investment Strategies</strong> – Whether you are building from scratch, buying an existing property, or renovating for high-value resale, we customize a strategy that aligns with your financial goals.</li>
      </ul>
      
      <h2>Making Your Move: How to Get Started</h2>
      <p>Investing in real estate from abroad is easier than ever with Lantena Property Managers. Here's how you can get started today:</p>
      
      <ul style="list-style-type: none; padding-left: 20px; margin-bottom: 20px;">
        <li>📌 <strong>Step 1:</strong> Schedule a Free Consultation – We'll discuss your investment goals and the best approach for your project.</li>
        <li>📌 <strong>Step 2:</strong> Feasibility Assessment – Our experts conduct market research, cost estimation, and project viability analysis.</li>
        <li>📌 <strong>Step 3:</strong> Project Planning – We create a step-by-step strategy, from legal approvals to construction timelines.</li>
        <li>📌 <strong>Step 4:</strong> Execution & Real-Time Monitoring – Watch your investment come to life while we handle all aspects of development.</li>
        <li>📌 <strong>Step 5:</strong> Property Management & ROI Maximization – Once complete, we ensure your property generates the highest possible returns with professional management services.</li>
      </ul>
      
      <h2>Conclusion: Secure Your Future with Lantena Property Managers</h2>
      <p>The future of real estate investment is smart, efficient, and technology-driven. Whether you are a first-time investor or a seasoned property owner, Lantena Property Managers provides you with the expertise, technology, and strategic solutions needed to succeed in the Kenyan market. Smart landlords don't work harder; they work smarter. And at Lantena, we make sure your investment works for you.</p>
      
      <div style="background-color: #f5f5f5; padding: 20px; border-left: 4px solid #f66b10; margin: 20px 0;">
        <h3 style="margin-top: 0; color: #0a233b;">📞 For a Free Consultation: Contact Lantena Property Managers Today!</h3>
        <p>☎ Call/WhatsApp: 0722575313 / 0736261808       📧 Email: lantenapmanagers@gmail.com<br>
        📍 Office Location: Ouru Complex, 3rd floor (Above Equity), Kisii<br>
        🌐 Website: <a href="https://www.lantenapm.com" style="color: #f66b10; text-decoration: none;">www.lantenapm.com</a></p>
      </div>
      </div>
    `
  }
];

// Property Management articles
const propertyManagementArticles = [
  {
    id: 'property-management-1',
    title: "The Future of Property Management: Why Smart Landlords Are Switching to Lantena",
    description: "Discover how the property management industry is transforming with technology and automation. Learn why forward-thinking landlords are embracing digital solutions for better returns and stress-free ownership.",
    image: "https://images.unsplash.com/photo-1560520031-3a4dc4e9de0c?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1073&q=80",
    author: "Lantena Team",
    readTime: "6 min read",
    date: "March 15, 2025",
    category: "Property Management",
    content: `
      <div style="font-size: 18px; line-height: 1.6;">
      <p>The property management industry is undergoing a transformation. Traditional methods of managing rental properties—chasing tenants for rent, handling maintenance manually, and keeping track of finances on paper—are becoming obsolete. The future of property management is digital, data-driven, and automated, and Lantena Property Managers is at the forefront of this revolution.</p>
      
      <h2>The Shift Towards Smart Property Management</h2>
      <p>Property management has long been seen as a hands-on, time-consuming task. Landlords used to personally collect rent, respond to tenant concerns, and manually handle financial records. However, in today's fast-paced world, property owners are looking for smarter, more efficient ways to manage their investments.</p>
      
      <h3>Key Industry Trends in Property Management:</h3>
      <ul style="list-style-type: none; padding-left: 20px; margin-bottom: 20px;">
        <li style="margin-bottom: 15px;">
          <div style="display: flex; align-items: flex-start;">
            <div style="min-width: 24px; height: 24px; background-color: #f66b10; border-radius: 50%; margin-right: 10px; display: flex; align-items: center; justify-content: center; color: white; font-weight: bold;">1</div>
            <div>
              <strong>Automation of Rent Collection</strong> – Gone are the days of knocking on doors or waiting for bank deposits. Smart landlords are leveraging automated rent collection systems that ensure timely payments, reducing delays and defaults.
            </div>
          </div>
        </li>
        <li style="margin-bottom: 15px;">
          <div style="display: flex; align-items: flex-start;">
            <div style="min-width: 24px; height: 24px; background-color: #f66b10; border-radius: 50%; margin-right: 10px; display: flex; align-items: center; justify-content: center; color: white; font-weight: bold;">2</div>
            <div>
              <strong>Cloud-Based Property Management</strong> – Digital platforms allow landlords to monitor their properties in real-time, track income and expenses, and get detailed reports without stepping foot on the property.
            </div>
          </div>
        </li>
        <li style="margin-bottom: 15px;">
          <div style="display: flex; align-items: flex-start;">
            <div style="min-width: 24px; height: 24px; background-color: #f66b10; border-radius: 50%; margin-right: 10px; display: flex; align-items: center; justify-content: center; color: white; font-weight: bold;">3</div>
            <div>
              <strong>Smart Maintenance Solutions</strong> – Maintenance requests can now be handled digitally, ensuring faster responses, efficient tracking, and minimized property damage due to delayed repairs.
            </div>
          </div>
        </li>
        <li style="margin-bottom: 15px;">
          <div style="display: flex; align-items: flex-start;">
            <div style="min-width: 24px; height: 24px; background-color: #f66b10; border-radius: 50%; margin-right: 10px; display: flex; align-items: center; justify-content: center; color: white; font-weight: bold;">4</div>
            <div>
              <strong>Data-Driven Decision Making</strong> – Real-time analytics help property owners understand rental trends, assess tenant reliability, and forecast revenue growth with precision.
            </div>
          </div>
        </li>
        <li style="margin-bottom: 15px;">
          <div style="display: flex; align-items: flex-start;">
            <div style="min-width: 24px; height: 24px; background-color: #f66b10; border-radius: 50%; margin-right: 10px; display: flex; align-items: center; justify-content: center; color: white; font-weight: bold;">5</div>
            <div>
              <strong>Enhanced Compliance Management</strong> – With government regulations like MRI tax requirements, property owners need professional services to ensure they stay compliant and avoid penalties.
            </div>
          </div>
        </li>
      </ul>
      
      <h2>Why Smart Landlords Choose Lantena</h2>
      <p>Lantena Property Managers is not just another property management company. We provide a complete, technology-driven solution that makes property ownership stress-free. Here's how we do it:</p>
      
      <ul style="list-style-type: none; padding-left: 20px; margin-bottom: 20px;">
        <li style="margin-bottom: 10px;">✅ <strong>Effortless Rent Collection</strong> – Our automated system ensures rent goes directly to your account, eliminating delays and losses.</li>
        <li style="margin-bottom: 10px;">✅ <strong>Real-Time Monitoring</strong> – Our cutting-edge property management software gives landlords access to real-time reports, tenant payment history, and unit balances from anywhere in the world.</li>
        <li style="margin-bottom: 10px;">✅ <strong>Maintenance Management</strong> – No more tenant complaints piling up! We handle all maintenance requests efficiently and ensure timely repairs, keeping tenants happy and properties in excellent condition.</li>
        <li style="margin-bottom: 10px;">✅ <strong>MRI Tax Support & Compliance</strong> – We help landlords stay fully compliant with Kenya Revenue Authority (KRA) regulations, ensuring taxes are filed accurately and on time.</li>
        <li style="margin-bottom: 10px;">✅ <strong>Professional Tenant Management</strong> – From screening new tenants to handling renewals and disputes, we take care of all the landlord-tenant interactions.</li>
      </ul>
      
      <h2>The Lantena Advantage: More Than Just Property Management</h2>
      
      <div style="background-color: #f5f5f5; border-radius: 8px; padding: 20px; margin: 20px 0;">
        <div style="display: flex; align-items: center; margin-bottom: 15px;">
          <div style="min-width: 40px; height: 40px; background-color: #0a233b; border-radius: 50%; margin-right: 15px; display: flex; align-items: center; justify-content: center;">
            <span style="color: #a6d1ea; font-size: 20px;">💰</span>
          </div>
          <div>
            <h3 style="margin: 0; color: #0a233b;">Maximized Rental Income</h3>
            <p style="margin: 5px 0 0 0;">Our data-driven approach helps landlords price their units competitively and minimize vacancies.</p>
          </div>
        </div>
        
        <div style="display: flex; align-items: center; margin-bottom: 15px;">
          <div style="min-width: 40px; height: 40px; background-color: #0a233b; border-radius: 50%; margin-right: 15px; display: flex; align-items: center; justify-content: center;">
            <span style="color: #a6d1ea; font-size: 20px;">🛠️</span>
          </div>
          <div>
            <h3 style="margin: 0; color: #0a233b;">Hassle-Free Operations</h3>
            <p style="margin: 5px 0 0 0;">With our full-suite management services, landlords can focus on growth while we handle day-to-day property operations.</p>
          </div>
        </div>
        
        <div style="display: flex; align-items: center; margin-bottom: 15px;">
          <div style="min-width: 40px; height: 40px; background-color: #0a233b; border-radius: 50%; margin-right: 15px; display: flex; align-items: center; justify-content: center;">
            <span style="color: #a6d1ea; font-size: 20px;">📊</span>
          </div>
          <div>
            <h3 style="margin: 0; color: #0a233b;">Transparent Financial Reporting</h3>
            <p style="margin: 5px 0 0 0;">Our structured financial reporting system ensures landlords have a clear overview of income, expenses, and ROI at all times.</p>
          </div>
        </div>
        
        <div style="display: flex; align-items: center;">
          <div style="min-width: 40px; height: 40px; background-color: #0a233b; border-radius: 50%; margin-right: 15px; display: flex; align-items: center; justify-content: center;">
            <span style="color: #a6d1ea; font-size: 20px;">📈</span>
          </div>
          <div>
            <h3 style="margin: 0; color: #0a233b;">Scalability for Investors</h3>
            <p style="margin: 5px 0 0 0;">Whether you own one unit or an entire apartment complex, our solutions scale with your portfolio.</p>
          </div>
        </div>
      </div>
      
      <h2>Take the Smart Step Today!</h2>
      <p>Why struggle with rent collection, tenant management, and compliance headaches when Lantena can handle it all? Join the revolution in property management and experience efficiency like never before.</p>
      
      <div style="background-color: #0a233b; color: white; padding: 25px; border-radius: 8px; margin: 30px 0; text-align: center;">
        <h3 style="margin-top: 0; color: #a6d1ea;">Ready to Transform Your Property Management Experience?</h3>
        <p style="margin-bottom: 20px;">Contact us today for a FREE consultation!</p>
        <p>📞 Call/WhatsApp: 0722575313 / 0736261808       📧 Email: lantenapmanagers@gmail.com<br>
        📍 Visit us at Ouru Complex, 3rd Floor (Above Equity), Kisii<br>
        🌐 Learn more at <a href="https://www.lantenapm.com" style="color: #f66b10; text-decoration: none;">www.lantenapm.com</a></p>
        <p style="font-weight: bold; color: #a6d1ea; margin-top: 20px;">Lantena Property Managers – Your Trusted, Accountable and Transparent Property and Project Partner</p>
      </div>
      </div>
    `
  }
];

// All articles combined
const allArticles = [...marketTrendsArticles, ...investmentArticles, ...propertyManagementArticles];

// Get category icon
const getCategoryIcon = (category) => {
  switch (category) {
    case 'Market Trends':
      return <TrendingUpIcon sx={{ fontSize: 16, mr: 0.5 }} />;
    case 'Investment':
      return <TrendingUpIcon sx={{ fontSize: 16, mr: 0.5 }} />;
    case 'Property Management':
      return <HomeWorkIcon sx={{ fontSize: 16, mr: 0.5 }} />;
    default:
      return null;
  }
};

const Article = () => {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);

  useEffect(() => {
    // Find the article with the matching ID
    const foundArticle = allArticles.find(article => article.id === id);

    if (foundArticle) {
      setArticle(foundArticle);

      // Find related articles (same category, excluding current article)
      const related = allArticles
        .filter(a => a.category === foundArticle.category && a.id !== id)
        .slice(0, 2); // Limit to 2 related articles

      setRelatedArticles(related);
    }

    // Scroll to top when article changes
    window.scrollTo(0, 0);
  }, [id]);

  if (!article) {
    return (
      <Box sx={{
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#fbfbfb',
        pt: { xs: 10, md: 12 }
      }}>
        <Typography variant="h5" color="text.secondary">
          Article not found
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{
      backgroundColor: '#fbfbfb',
      minHeight: '100vh',
      pt: { xs: 10, md: 12 },
      pb: 8
    }}>
      <Container maxWidth="lg">
        {/* Breadcrumbs Navigation */}
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link to="/" style={{ color: '#6a6565', textDecoration: 'none' }}>
            Home
          </Link>
          <Link to="/community-blog" style={{ color: '#6a6565', textDecoration: 'none' }}>
            Community Blog
          </Link>
          <Typography color="text.primary">{article.title}</Typography>
        </Breadcrumbs>

        {/* Back Button */}
        <Button
          component={Link}
          to="/community-blog"
          startIcon={<ArrowBackIcon />}
          sx={{
            mb: 3,
            color: '#0a233b',
            '&:hover': { color: '#f66b10' }
          }}
        >
          Back to Articles
        </Button>

        {/* Article Header */}
        <Box sx={{ mb: 4 }}>
          <Chip
            icon={getCategoryIcon(article.category)}
            label={article.category}
            size="small"
            sx={{
              mb: 2,
              backgroundColor: article.category === 'Market Trends' ? 'rgba(166, 209, 234, 0.2)' : article.category === 'Investment' ? 'rgba(166, 209, 234, 0.2)' : 'rgba(246, 107, 16, 0.1)',
              color: article.category === 'Market Trends' ? '#0a233b' : article.category === 'Investment' ? '#0a233b' : '#f66b10',
              fontWeight: 500,
              '& .MuiChip-icon': {
                color: 'inherit'
              }
            }}
          />
          <Typography
            variant="h3"
            component="h1"
            sx={{
              fontWeight: 700,
              color: '#0a233b',
              mb: 2,
              fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.5rem' }
            }}
          >
            {article.title}
          </Typography>

          {/* Article Meta */}
          <Box sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 3,
            mb: 3
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Avatar
                sx={{
                  width: 40,
                  height: 40,
                  backgroundColor: '#0a233b',
                  mr: 1.5
                }}
              >
                {article.author.charAt(0)}
              </Avatar>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#0a233b' }}>
                  {article.author}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Author
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <CalendarTodayIcon sx={{ fontSize: 18, mr: 1, color: '#6a6565' }} />
              <Typography variant="body2" color="text.secondary">
                {article.date}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <AccessTimeIcon sx={{ fontSize: 18, mr: 1, color: '#6a6565' }} />
              <Typography variant="body2" color="text.secondary">
                {article.readTime}
              </Typography>
            </Box>

            <Button
              startIcon={<ShareIcon />}
              size="small"
              sx={{
                ml: 'auto',
                color: '#0a233b',
                '&:hover': { color: '#f66b10' }
              }}
            >
              Share
            </Button>
          </Box>
        </Box>

        {/* Featured Image */}
        <Box
          component="img"
          src={article.image}
          alt={article.title}
          sx={{
            width: '100%',
            height: { xs: '200px', sm: '300px', md: '400px' },
            objectFit: 'cover',
            borderRadius: 2,
            mb: 4
          }}
        />

        {/* Article Content */}
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 4
        }}>
          {/* Main Content */}
          <Box
            sx={{
              flex: 1,
              '& h2': {
                color: '#0a233b',
                fontWeight: 600,
                fontSize: '1.5rem',
                mt: 4,
                mb: 2
              },
              '& p': {
                color: '#6a6565',
                mb: 2,
                lineHeight: 1.7
              }
            }}
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          {/* Sidebar */}
          <Box sx={{ width: { xs: '100%', md: '300px' } }}>
            <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: '#0a233b',
                  mb: 2,
                  pb: 1,
                  borderBottom: '2px solid #a6d1ea'
                }}
              >
                Related Articles
              </Typography>

              {relatedArticles.length > 0 ? (
                relatedArticles.map((relatedArticle) => (
                  <Box
                    key={relatedArticle.id}
                    component={Link}
                    to={`/article/${relatedArticle.id}`}
                    sx={{
                      display: 'flex',
                      mb: 2,
                      textDecoration: 'none',
                      '&:hover h3': {
                        color: '#f66b10'
                      }
                    }}
                  >
                    <Box
                      component="img"
                      src={relatedArticle.image}
                      alt={relatedArticle.title}
                      sx={{
                        width: 80,
                        height: 60,
                        objectFit: 'cover',
                        borderRadius: 1,
                        mr: 2
                      }}
                    />
                    <Box>
                      <Typography
                        variant="subtitle2"
                        component="h3"
                        sx={{
                          fontWeight: 600,
                          color: '#0a233b',
                          transition: 'color 0.2s',
                          fontSize: '0.9rem',
                          mb: 0.5
                        }}
                      >
                        {relatedArticle.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {relatedArticle.readTime}
                      </Typography>
                    </Box>
                  </Box>
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No related articles found.
                </Typography>
              )}

              <Divider sx={{ my: 3 }} />

              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: '#0a233b',
                  mb: 2
                }}
              >
                Need Help?
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Our team of experts is ready to assist you with any property management questions.
              </Typography>
              <Button
                variant="contained"
                component={Link}
                to="/contact"
                fullWidth
                sx={{
                  backgroundColor: '#f66b10',
                  '&:hover': {
                    backgroundColor: '#d45500',
                  },
                  textTransform: 'none',
                  borderRadius: 2,
                  py: 1
                }}
              >
                Contact Us
              </Button>
            </Paper>
          </Box>
        </Box>

        {/* More Articles Section */}
        <Box sx={{ mt: 8 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 600,
              color: '#0a233b',
              mb: 4,
              textAlign: 'center'
            }}
          >
            More Articles
          </Typography>

          <Grid container spacing={3}>
            {allArticles
              .filter(a => a.id !== id)
              .slice(0, 3)
              .map((article) => (
                <Grid item xs={12} sm={6} md={4} key={article.id}>
                  <Paper
                    elevation={1}
                    sx={{
                      borderRadius: 2,
                      overflow: 'hidden',
                      transition: 'transform 0.3s, box-shadow 0.3s',
                      '&:hover': {
                        transform: 'translateY(-5px)',
                        boxShadow: 4,
                      },
                    }}
                  >
                    <Box
                      component={Link}
                      to={`/article/${article.id}`}
                      sx={{
                        display: 'block',
                        textDecoration: 'none'
                      }}
                    >
                      <Box
                        component="img"
                        src={article.image}
                        alt={article.title}
                        sx={{
                          width: '100%',
                          height: 180,
                          objectFit: 'cover'
                        }}
                      />
                      <Box sx={{ p: 2 }}>
                        <Chip
                          icon={getCategoryIcon(article.category)}
                          label={article.category}
                          size="small"
                          sx={{
                            mb: 1.5,
                            backgroundColor: article.category === 'Market Trends' ? 'rgba(166, 209, 234, 0.2)' : article.category === 'Investment' ? 'rgba(166, 209, 234, 0.2)' : 'rgba(246, 107, 16, 0.1)',
                            color: article.category === 'Market Trends' ? '#0a233b' : article.category === 'Investment' ? '#0a233b' : '#f66b10',
                            fontWeight: 500,
                            '& .MuiChip-icon': {
                              color: 'inherit'
                            }
                          }}
                        />
                        <Typography
                          variant="subtitle1"
                          component="h3"
                          sx={{
                            fontWeight: 600,
                            color: '#0a233b',
                            mb: 1,
                            height: '3rem',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                          }}
                        >
                          {article.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {article.date} • {article.readTime}
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
              ))}
          </Grid>
        </Box>
      </Container>
    </Box>
  );
};

export default Article;
